/**
 * PhaseSpine — the eight-phase method spine.
 *
 * Semantic phase iconography from the Ekselens design system (v1.1). On dark
 * surfaces the accent is lilac (#C9B8FB, AAA on charcoal); purple (#7737F6) is
 * fill-only on charcoal per the brand guidelines. Icons are stroke-only
 * `currentColor`, so colour is set via CSS. On white surfaces (phase cards) the
 * icons use brand purple, which is compliant on light backgrounds.
 *
 * Exports:
 *   PHASE_ICONS  — map keyed by phase name → icon component ({ className })
 *   PhaseSpine   — horizontal 8-node method strip (dark surface, purple accents)
 */
import React from "react";

const svgProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.75,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": "true",
};

const Discover = ({ className }) => (
  <svg className={className} {...svgProps}>
    <circle cx="10.5" cy="10.5" r="5.75" />
    <line x1="14.75" y1="14.75" x2="19.5" y2="19.5" />
    <circle cx="10.5" cy="10.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const Diagnose = ({ className }) => (
  <svg className={className} {...svgProps}>
    <rect x="3.5" y="6" width="17" height="12" rx="2" />
    <polyline points="6,12 9,12 10.5,8.5 13,15.5 14.5,12 18,12" />
  </svg>
);

const Design = ({ className }) => (
  <svg className={className} {...svgProps}>
    <path d="M 4 3.5 L 16 3.5 L 20 7.5 L 20 20.5 L 4 20.5 Z" />
    <polyline points="16,3.5 16,7.5 20,7.5" />
    <line x1="8" y1="11" x2="16" y2="11" />
    <line x1="8" y1="14.5" x2="13" y2="14.5" />
    <circle cx="15.25" cy="16.5" r="1.25" />
  </svg>
);

const Plan = ({ className }) => (
  <svg className={className} {...svgProps}>
    <rect x="3.75" y="5.75" width="7.5" height="2.5" rx="1.25" />
    <rect x="8" y="10.75" width="9" height="2.5" rx="1.25" />
    <rect x="13.25" y="15.75" width="7" height="2.5" rx="1.25" />
  </svg>
);

const Mobilise = ({ className }) => (
  <svg className={className} {...svgProps}>
    <polyline points="4.5,8 8,12 4.5,16" />
    <polyline points="10.5,8 14,12 10.5,16" />
    <polyline points="16.5,8 20,12 16.5,16" />
  </svg>
);

const Deliver = ({ className }) => (
  <svg className={className} {...svgProps}>
    <rect x="4" y="7" width="16" height="12" rx="1.5" />
    <line x1="4" y1="11" x2="20" y2="11" />
    <polyline points="9,15 11,17 15,13" />
  </svg>
);

const Embed = ({ className }) => (
  <svg className={className} {...svgProps}>
    <rect x="3.5" y="3.5" width="17" height="17" rx="2" />
    <circle cx="12" cy="8" r="1.75" />
    <line x1="12" y1="9.75" x2="12" y2="17" />
    <path d="M 8 13.5 Q 8 17 12 17 Q 16 17 16 13.5" />
  </svg>
);

const Optimise = ({ className }) => (
  <svg className={className} {...svgProps}>
    <line x1="4" y1="6.5" x2="20" y2="6.5" />
    <line x1="4" y1="12" x2="20" y2="12" />
    <line x1="4" y1="17.5" x2="20" y2="17.5" />
    <circle cx="9" cy="6.5" r="1.35" fill="currentColor" stroke="none" />
    <circle cx="15" cy="12" r="1.35" fill="currentColor" stroke="none" />
    <circle cx="11" cy="17.5" r="1.35" fill="currentColor" stroke="none" />
  </svg>
);

export const PHASE_ICONS = {
  Discover,
  Diagnose,
  Design,
  Plan,
  Mobilise,
  Deliver,
  Embed,
  Optimise,
};

const SPINE = [
  { n: "01", name: "Discover" },
  { n: "02", name: "Diagnose" },
  { n: "03", name: "Design" },
  { n: "04", name: "Plan" },
  { n: "05", name: "Mobilise" },
  { n: "06", name: "Deliver" },
  { n: "07", name: "Embed" },
  { n: "08", name: "Optimise" },
];

/**
 * PhaseSpine — compact horizontal strip of the eight phases.
 * Dark ink surface with purple-accented pill nodes (brand-aligned).
 */
export const PhaseSpine = ({ className = "" }) => (
  <div
    className={`grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 rounded-xl bg-[#0B0D12] border border-[#A084F1]/50 p-4 ${className}`}
    aria-label="Ekselens method spine"
    data-testid="phase-spine"
  >
    {SPINE.map(({ n, name }) => {
      const Icon = PHASE_ICONS[name];
      return (
        <div
          key={n}
          className="group flex items-center gap-2 rounded-full bg-[#221D3A] border border-[#A084F1]/70 px-3 py-2 transition-[transform,border-color] duration-200 ease-[cubic-bezier(0.2,0.8,0.2,1)] hover:-translate-y-0.5 hover:border-[#C9B8FB]"
          data-testid={`spine-node-${n}`}
        >
          <Icon className="w-5 h-5 shrink-0 text-[#C9B8FB] transition-colors duration-200 group-hover:text-[#D8CBFF]" />
          <span className="text-[11px] font-bold tabular-nums text-[#C9B8FB]">{n}</span>
          <span className="text-[12px] font-semibold text-white truncate">{name}</span>
        </div>
      );
    })}
  </div>
);

export default PhaseSpine;
